
import {ø} from "../../util/objects.js";
import * as utc from "../../util/utc.js";
import {resolveTimeCursor} from "../../util/dates.js";
import {scalarProduct} from "../scalarProduct.js";
import * as sources from "../sources.js";
import {newProduct, resolveValidTime} from "../productUtils.js";
import {tr} from "../../ux/translations.js";
import {createUnitDescriptors} from "../../util/units.js";
import {gaiaPath} from "../gaia.js";
import {createSSTAPalette} from "../../palette/ssta.js";
import {createSSTWithIcePalette} from "../../palette/sstice.js";


const SST = "sea_surface_temp";
const SSTA = "sea_surface_temp_anomaly";

let palettes = {};

function recipeFor(attr, name) {
    if (name === SST) {
        return attr.hd_enabled ?
            {tag: SST, res: "0.1", selector: /analysed_sst/} :
            {tag: SST, res: "0.5", selector: /analysed_sst/};
    }
    if (name === SSTA) {
        return attr.hd_enabled ?
            {tag: SSTA, res: "0.25", selector: /sst_anomaly/} :
            {tag: SSTA, res: "0.5",  selector: /sst_anomaly/};
    }
}

export function ostiaPath(attr, name) {
    // 2019/12/20191215-sst-ostia-0.1.epak
    // 2019/12/20191215-ssta-ostia-0.25.epak
    // current/current-sst-ostia-0.1.epak

    const recipe = recipeFor(attr, name);
    const parts = resolveTimeCursor(attr.time_cursor);
    const isNow = attr.time_cursor === "now";
    const dir = isNow ? "current" : utc.print(parts, "{YYYY}/{MM}");
    const timestamp = isNow ? "current" : utc.print(parts, "{YYYY}{MM}{DD}");
    const file = `${timestamp}-${recipe.tag}-ostia-${recipe.res}.epak`;
    return gaiaPath("data/ostia", dir, file);
}

function newOstiaProduct(attr, timeline) {
    // Each day, SST data for the previous day is made available. So "now" means yesterday.
    const resolvedTimeCursor = utc.add(resolveTimeCursor(attr.time_cursor), {day: attr.time_cursor === "now" ? -1 : 0});

    const alignment = {day: 1};
    const validTime = resolveValidTime(resolvedTimeCursor, alignment);
    const stepper = step => function() {
        return resolveValidTime(utc.add(this.validTime(), step), alignment);
    };
    return {
        sourceHTML: sources.ostia,
        validTime: () => ø(validTime),
        timeline,
        prev2: stepper({day: -5}),
        prev1: stepper({day: -1}),
        next1: stepper({day: +1}),
        next2: stepper({day: +5}),
        navLabels: {
            prev2: `-5 ${tr`days`}`,
            prev1: `-1 ${tr`day`}`,
            next1: `+1 ${tr`day`}`,
            next2: `+5 ${tr`days`}`,
        },
    };
}

export function createOstiaSST(attr, timeline) {
    return ø(newProduct(), newOstiaProduct(attr, timeline), {
        type: SST,
        descriptionHTML: {
            name: tr`Sea Surface Temperature`,
            qualifier: "",
        },
        paths: [ostiaPath(attr, SST)],
        builder: file => scalarProduct(file, /analysed_sst/, {hasMissing: true}),
        unitDescriptors: createUnitDescriptors({
            "°C": {convert: x => x - 273.15,       precision: 1},
            "°F": {convert: x => x * 9/5 - 459.67, precision: 1},
            "K":  {convert: x => x,                precision: 1},
        }),
        scale: palettes.sst ??= createSSTWithIcePalette(),
        alpha: {single: 200, animated: 132},
    });
}

export function createOstiaSSTA(attr, timeline) {
    return ø(newProduct(), newOstiaProduct(attr, timeline), {
        type: SSTA,
        descriptionHTML: {
            name: tr`Sea Surface Temperature Anomaly`,
            qualifier: "",
        },
        paths: [ostiaPath(attr, SSTA)],
        builder: file => scalarProduct(file, /sst_anomaly/, {hasMissing: true}),
        unitDescriptors: createUnitDescriptors({
            "°C": {convert: x => x,       precision: 1},
            "°F": {convert: x => x * 9/5, precision: 1},
            "K":  {convert: x => x,       precision: 1},
        }),
        scale: palettes.ssta ??= createSSTAPalette(),
    });
}
